import React, { useState, useEffect } from 'react';
import {
  CheckCircleIcon,
  XCircleIcon,
  ArrowUturnLeftIcon,
  ClockIcon,
  UserIcon,
  CalendarIcon,
  DocumentTextIcon,
  ChevronRightIcon
} from '@heroicons/react/24/outline';
import { useAuth } from '../contexts/AuthContext';

const PendingApprovals = () => {
  const [pendingApprovals, setPendingApprovals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedApproval, setSelectedApproval] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [comments, setComments] = useState('');
  const { user } = useAuth();

  useEffect(() => {
    loadPendingApprovals();
  }, []);

  const loadPendingApprovals = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('authToken');
      const response = await fetch('/api/workflow/approvals/pending', {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setPendingApprovals(data.data || []);
      }
    } catch (error) {
      console.error('Error loading pending approvals:', error);
    } finally {
      setLoading(false);
    }
  };

  const processApproval = async (submissionId, action) => {
    try {
      setActionLoading(true);
      const token = localStorage.getItem('authToken');
      
      const response = await fetch(`/api/workflow/approvals/${submissionId}/process`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          action,
          comments: comments.trim() || null
        })
      });

      if (response.ok) {
        // Refresh pending approvals
        await loadPendingApprovals();
        setSelectedApproval(null);
        setComments('');
        
        // Show success message
        alert(`Review ${action}d successfully!`);
      } else {
        const error = await response.json();
        alert(`Error: ${error.message}`);
      }
    } catch (error) {
      console.error('Error processing approval:', error);
      alert('Error processing approval');
    } finally {
      setActionLoading(false);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusColor = (recommendation) => {
    switch (recommendation) {
      case 'confirm':
        return 'bg-green-100 text-green-800 border-green-200';
      case 'extend':
        return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'terminate':
        return 'bg-red-100 text-red-800 border-red-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white shadow rounded-lg p-6">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Pending Approvals</h1>
            <p className="mt-1 text-sm text-gray-500">
              Review and process pending approval requests ({pendingApprovals.length} pending)
            </p>
          </div>
          
          <div className="flex items-center space-x-2 text-sm text-gray-500">
            <ClockIcon className="h-5 w-5" />
            <span>Last updated: {new Date().toLocaleTimeString()}</span>
          </div>
        </div>
      </div>

      {/* Approvals List */}
      <div className="bg-white shadow rounded-lg overflow-hidden">
        {pendingApprovals.length === 0 ? (
          <div className="text-center py-12">
            <CheckCircleIcon className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">No pending approvals</h3>
            <p className="mt-1 text-sm text-gray-500">
              All approval requests have been processed.
            </p>
          </div>
        ) : (
          <div className="divide-y divide-gray-200">
            {pendingApprovals.map((approval) => (
              <div
                key={approval.id}
                className="p-6 hover:bg-gray-50 cursor-pointer"
                onClick={() => setSelectedApproval(selectedApproval?.id === approval.id ? null : approval)}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-4">
                    <div className="flex-shrink-0">
                      <div className="h-10 w-10 bg-blue-100 rounded-full flex items-center justify-center">
                        <UserIcon className="h-6 w-6 text-blue-600" />
                      </div>
                    </div>
                    
                    <div>
                      <h3 className="text-sm font-medium text-gray-900">
                        {approval.first_name} {approval.last_name}
                      </h3>
                      <p className="text-sm text-gray-500">
                        {approval.employee_number} • {approval.job_title}
                      </p>
                      <p className="text-xs text-gray-400 mt-1">
                        Step: {approval.step_name} ({approval.workflow_name})
                      </p>
                    </div>
                  </div>
                  
                  <div className="flex items-center space-x-4">
                    <div className="text-right">
                      <div className={`inline-flex px-2 py-1 text-xs rounded-full border ${getStatusColor(approval.recommendation)}`}>
                        {approval.recommendation?.charAt(0).toUpperCase() + approval.recommendation?.slice(1)}
                      </div>
                      <p className="text-xs text-gray-500 mt-1">
                        Submitted: {formatDate(approval.submitted_at)}
                      </p>
                    </div>
                    
                    <ChevronRightIcon
                      className={`h-5 w-5 text-gray-400 transition-transform ${
                        selectedApproval?.id === approval.id ? 'transform rotate-90' : ''
                      }`}
                    />
                  </div>
                </div>

                {/* Expanded Details */}
                {selectedApproval?.id === approval.id && (
                  <div className="mt-6 border-t border-gray-200 pt-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      {/* Review Details */}
                      <div>
                        <h4 className="text-sm font-medium text-gray-900 mb-3">Review Details</h4>
                        <div className="space-y-2 text-sm">
                          <div className="flex justify-between">
                            <span className="text-gray-500">Overall Rating:</span>
                            <span className="font-medium">{approval.overall_rating}/5</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-gray-500">Reviewer:</span>
                            <span className="font-medium">
                              {approval.reviewer_first_name} {approval.reviewer_last_name}
                            </span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-gray-500">Recommendation Reason:</span>
                            <span className="font-medium text-right max-w-xs">
                              {approval.recommendation_reason || 'Not provided'}
                            </span>
                          </div>
                        </div>
                      </div>

                      {/* Action Section */}
                      <div>
                        <h4 className="text-sm font-medium text-gray-900 mb-3">Take Action</h4>
                        
                        <textarea
                          value={comments}
                          onChange={(e) => setComments(e.target.value)}
                          placeholder="Add comments (optional)..."
                          className="w-full h-20 px-3 py-2 border border-gray-300 rounded-md text-sm resize-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        />
                        
                        <div className="flex space-x-2 mt-3">
                          <button
                            onClick={() => processApproval(approval.id, 'approve')}
                            disabled={actionLoading}
                            className="flex items-center space-x-1 px-3 py-2 bg-green-600 text-white text-sm font-medium rounded-md hover:bg-green-700 disabled:opacity-50"
                          >
                            <CheckCircleIcon className="h-4 w-4" />
                            <span>Approve</span>
                          </button>
                          
                          <button
                            onClick={() => processApproval(approval.id, 'reject')}
                            disabled={actionLoading}
                            className="flex items-center space-x-1 px-3 py-2 bg-red-600 text-white text-sm font-medium rounded-md hover:bg-red-700 disabled:opacity-50"
                          >
                            <XCircleIcon className="h-4 w-4" />
                            <span>Reject</span>
                          </button>
                          
                          <button
                            onClick={() => processApproval(approval.id, 'return')}
                            disabled={actionLoading}
                            className="flex items-center space-x-1 px-3 py-2 bg-yellow-600 text-white text-sm font-medium rounded-md hover:bg-yellow-700 disabled:opacity-50"
                          >
                            <ArrowUturnLeftIcon className="h-4 w-4" />
                            <span>Return</span>
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default PendingApprovals;